import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import { email, profileUrls } from '@config';

// https://www.gatsbyjs.com/docs/add-seo-component/

const Head = ({ title, description, image }) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            defaultDescription: description
            siteUrl
            defaultImage: image
            twitterUsername
          }
        }
      }
    `,
  );

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image || defaultImage}`,
    url: `${siteUrl}${pathname}`,
  };

  // Tells Google this site *is* the person, rather than a page that merely
  // mentions the name. Matters more than usual here: there is an unrelated
  // Wyatt Olsen with an older domain, and `sameAs` is how the two entities get
  // told apart.
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Wyatt Olsen',
    url: siteUrl,
    image: `${siteUrl}${defaultImage}`,
    email: `mailto:${email}`,
    description: defaultDescription,
    jobTitle: 'Finance and Risk Management & Insurance student',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Iowa City',
      addressRegion: 'IA',
      addressCountry: 'US',
    },
    affiliation: {
      '@type': 'CollegeOrUniversity',
      name: 'University of Iowa, Tippie College of Business',
      sameAs: 'https://tippie.uiowa.edu/',
    },
    alumniOf: {
      '@type': 'CollegeOrUniversity',
      name: 'Kirkwood Community College',
    },
    knowsAbout: ['Risk management', 'Insurance', 'Finance', 'AI-assisted software development'],
    sameAs: profileUrls,
  };

  return (
    <Helmet title={title} defaultTitle={seo.title} titleTemplate={`%s | ${defaultTitle}`}>
      <html lang="en" />

      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />

      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      <link rel="canonical" href={seo.url} />

      <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
};

Head.defaultProps = {
  title: null,
  description: null,
  image: null,
};
